import type { ApiClient } from '../client';
import { mapAddress, type RawAddress, type ShippingAddress } from './addresses';

const BASE = '/loot_machine/api/game_platform/iframe/v1/pool_shippings';

export type PoolShippingType = 'delivery' | 'smc_transfer' | (string & {});
export type PoolShippingStatus =
  | 'pending'
  | 'canceled'
  | 'shipped'
  | 'delivered'
  | (string & {});

export interface IframePoolShippingInvoice {
  id: number;
  status: string | null;
  totalAmount: number | null;
  paidAt: string | null;
}

export interface IframePoolShipping {
  id: number;
  status: PoolShippingStatus;
  shippingType: PoolShippingType;
  humanizedStatus: string;
  createdAt: string;
  poolItemsCount: number;
  invoice: IframePoolShippingInvoice | null;
  /** First item's first image — the deliveries-list card preview. Null when
   *  the item has no image. */
  previewImageUrl: string | null;
}

export interface IframePoolShippingsMeta {
  totalCount: number;
  page: number;
  per: number;
  activeOutCount: number;
}

export interface IframePoolShippingsPage {
  shippings: IframePoolShipping[];
  meta: IframePoolShippingsMeta;
}

export interface IframePoolShippingsParams {
  page?: number;
  per?: number;
}

/** Item attached to a created pool_shipping. The Show page renders one
 *  card per row in the Shipping List grid. */
export interface IframePoolShippingItem {
  id: number;
  title: string;
  imageUrl: string | null;
  fairMarketValue: number | null;
}

/** Extra paid line under the chosen shipping service — e.g. insurance,
 *  signature confirmation. Rendered with a check icon under the service
 *  card on the Show page. */
export interface IframePoolShippingPosition {
  id: number;
  name: string;
  price: number | null;
}

/** Detailed view of a single pool_shipping — drives the Show page.
 *  Extends `IframePoolShipping` (the list-row shape) with the fields
 *  only the detail endpoint returns (address, items, totals, etc). */
export interface IframePoolShippingDetail extends IframePoolShipping {
  address: ShippingAddress | null;
  items: IframePoolShippingItem[];
  service: PoolShippingService | null;
  positions: IframePoolShippingPosition[];
  salesTax: number;
  totalAmount: number;
  isCanCancel: boolean;
}

/** One row from `pool_shipping_services` — rendered in the Delivery
 *  Shipping Options section. */
export interface PoolShippingService {
  id: number;
  name: string;
  description: string | null;
  /** Optional flat fee for the service in USD; surfaced as a fee row
   *  when the backend ships it. */
  price: number | null;
}

/**
 * Initial values returned by `GET /pool_shippings/new` — the shipping
 * service catalogue the Delivery form renders as options.
 */
export interface NewPoolShippingResponse {
  services: PoolShippingService[];
}

/** Fee figures for the reveal Create Shipping modal. `handlingFee` is the
 *  flat handling charge; `shippingFee` / `salesTax` stay null until a
 *  delivery address is known (address-dependent quote). `note` is the
 *  backend-generated confirm-by / auto-buyback warning copy (deadline +
 *  buyback % filled in from the operator's config) — null when auto-sell is
 *  disabled. */
export interface ShippingFees {
  handlingFee: number | null;
  shippingFee: number | null;
  salesTax: number | null;
  note: string | null;
  /** Full EasyPost price per Delivery Shipping Option, keyed by service id —
   *  what the radio list shows. Empty until items + address make it quotable. */
  optionPrices: Record<number, number>;
}

/** Optional inputs that unlock the address-dependent figures: `addressId`
 *  enables the NumeralHQ sales-tax quote; `serviceIds` are the chosen
 *  Delivery Shipping Option(s) whose flat price becomes the shipping fee. */
export interface ShippingFeesParams {
  addressId?: number | null;
  serviceIds?: (number | string)[];
}

/** Body params for `POST /pool_shippings`. Maps to the Rails strong-params
 *  + nested-attributes contract. */
export interface CreatePoolShippingParams {
  addressId: number;
  poolItemIds: (number | string)[];
  poolShippingServiceIds: number[];
}

interface RawInvoice {
  id?: number | string;
  status?: string | null;
  total_amount?: number | string | null;
  paid_at?: string | null;
}

interface RawShipping {
  id?: number | string;
  status?: string;
  shipping_type?: string;
  humanized_status?: string;
  created_at?: string;
  pool_items_count?: number | string;
  pool_shipping_invoice?: RawInvoice | null;
  preview_image_url?: string | null;
}

interface RawMeta {
  total_count?: number | string;
  page?: number | string;
  per?: number | string;
  active_out_count?: number | string;
}

interface RawPage {
  pool_shippings?: RawShipping[];
  meta?: RawMeta;
}

interface RawShippingService {
  id?: number | string;
  name?: string;
  description?: string | null;
  price?: number | string | null;
}

interface RawCreatedShipping {
  pool_shipping?: RawShipping & { id?: number | string };
}

function mapInvoice(raw: RawInvoice | null | undefined): IframePoolShippingInvoice | null {
  if (!raw || raw.id === undefined || raw.id === null) return null;
  return {
    id: Number(raw.id),
    status: raw.status ?? null,
    totalAmount:
      raw.total_amount !== undefined && raw.total_amount !== null
        ? Number(raw.total_amount)
        : null,
    paidAt: raw.paid_at ?? null,
  };
}

function mapShipping(raw: RawShipping): IframePoolShipping {
  return {
    id: Number(raw.id ?? 0),
    status: (raw.status ?? 'pending') as PoolShippingStatus,
    shippingType: (raw.shipping_type ?? 'delivery') as PoolShippingType,
    humanizedStatus: raw.humanized_status ?? '',
    createdAt: raw.created_at ?? '',
    poolItemsCount: Number(raw.pool_items_count ?? 0),
    invoice: mapInvoice(raw.pool_shipping_invoice),
    previewImageUrl: raw.preview_image_url ?? null,
  };
}

function mapPage(raw: RawPage): IframePoolShippingsPage {
  const shippings = (raw.pool_shippings ?? []).map(mapShipping);
  return {
    shippings,
    meta: {
      totalCount: Number(raw.meta?.total_count ?? shippings.length),
      page: Number(raw.meta?.page ?? 1),
      per: Number(raw.meta?.per ?? shippings.length),
      activeOutCount: Number(raw.meta?.active_out_count ?? 0),
    },
  };
}

function mapService(raw: RawShippingService): PoolShippingService {
  return {
    id: Number(raw.id ?? 0),
    name: raw.name ?? '',
    description: raw.description ?? null,
    price: raw.price !== undefined && raw.price !== null ? Number(raw.price) : null,
  };
}

/** GET /pool_shippings — the player's deliveries (Deliveries tab). */
export async function listPoolShippings(
  client: ApiClient,
  params: IframePoolShippingsParams = {},
): Promise<IframePoolShippingsPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await client.request<RawPage | undefined>(`${BASE}${suffix}`, { method: 'GET' });
  return mapPage(raw ?? {});
}

/** One shipped item as the v1 ShowSerializer emits it (PoolItems::ShortSerializer):
 *  `images` is an array of objects; FMV isn't included. */
interface RawShowItem {
  id?: number | string;
  title?: string;
  images?: Array<{ url?: string | null }> | null;
}

/** One position row (PoolShippingPositions::IndexSerializer). */
interface RawShowPosition {
  id?: number | string;
  name?: string;
  description?: string | null;
  price?: number | string | null;
}

/** The detail payload under root `pool_shipping` — extends the list-row shape
 *  with the detail-only fields the v1 ShowSerializer adds. */
interface RawShowShipping extends RawShipping {
  address?: RawAddress | null;
  pool_items?: RawShowItem[];
  pool_shipping_positions?: RawShowPosition[];
  is_can_cancel?: boolean;
}

/**
 * GET /pool_shippings/:id — detail for the Delivery Show page.
 *
 * Maps the live `Shared::V1::PoolShippings::ShowSerializer` shape (root
 * `pool_shipping`): `address` is a raw Address record (reuse `mapAddress`),
 * items come under `pool_items` with an `images[]` array, and positions under
 * `pool_shipping_positions`. The serializer has no top-level service object,
 * so we synthesize one from the first position (the chosen delivery option) —
 * otherwise the Show page hides the positions block. FMV / sales tax aren't in
 * this payload (not shown on the Show page); total is summed from positions.
 */
export async function getPoolShipping(
  client: ApiClient,
  id: number,
): Promise<IframePoolShippingDetail> {
  const raw = await client.request<{ pool_shipping?: RawShowShipping } | undefined>(
    `${BASE}/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  const ps = raw?.pool_shipping ?? {};
  const base = mapShipping(ps);

  const items: IframePoolShippingItem[] = (ps.pool_items ?? []).map((it) => ({
    id: Number(it.id ?? 0),
    title: it.title ?? '',
    imageUrl: it.images?.[0]?.url ?? null,
    fairMarketValue: null,
  }));

  const positions: IframePoolShippingPosition[] = (ps.pool_shipping_positions ?? []).map((p) => ({
    id: Number(p.id ?? 0),
    name: p.name ?? '',
    price: p.price !== undefined && p.price !== null ? Number(p.price) : null,
  }));

  const first = positions[0];
  const service: PoolShippingService | null = first
    ? { id: first.id, name: first.name, description: null, price: first.price }
    : null;

  const totalAmount = positions.reduce((sum, p) => sum + (p.price ?? 0), 0);

  return {
    ...base,
    poolItemsCount: items.length,
    address: ps.address ? mapAddress(ps.address) : null,
    items,
    service,
    positions,
    salesTax: 0,
    totalAmount,
    isCanCancel: ps.is_can_cancel ?? base.status === 'pending',
  };
}

/** GET /pool_shippings/new — shipping-service catalogue for the form. */
export async function getNewShipping(client: ApiClient): Promise<NewPoolShippingResponse> {
  const raw = await client.request<{ result?: { services?: RawShippingService[] } } | undefined>(
    `${BASE}/new`,
    { method: 'GET' },
  );
  const services = (raw?.result?.services ?? []).map(mapService);
  return { services };
}

/**
 * GET /pool_shippings/fees?pool_item_ids[]=… — fee figures for the reveal
 * modal, quoted for the given items. Today the backend returns the
 * `handling_fee` (per-item × count); `shipping_fee` / `sales_tax` arrive once
 * the endpoint also takes a delivery address.
 */
export async function getShippingFees(
  client: ApiClient,
  poolItemIds: (number | string)[] = [],
  params: ShippingFeesParams = {},
): Promise<ShippingFees> {
  const qs = new URLSearchParams();
  for (const id of poolItemIds) qs.append('pool_item_ids[]', String(id));
  // address unlocks the NumeralHQ sales-tax quote; service ids price the
  // shipping fee. Both optional — without them the backend returns "TBD".
  if (params.addressId != null) qs.set('address_id', String(params.addressId));
  for (const id of params.serviceIds ?? [])
    qs.append('pool_shipping_service_ids[]', String(id));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await client.request<
    | {
        result?: {
          handling_fee?: number | string | null;
          shipping_fee?: number | string | null;
          sales_tax?: number | string | null;
          note?: string | null;
          option_prices?: Record<string, number | string | null> | null;
        };
      }
    | undefined
  >(`${BASE}/fees${suffix}`, { method: 'GET' });
  const r = raw?.result;
  // Tolerate non-numeric sentinels (e.g. handling's 'Service not found') →
  // null, so callers show a placeholder instead of "$NaN".
  const num = (v: number | string | null | undefined): number | null => {
    if (v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const optionPrices: Record<number, number> = {};
  for (const [id, value] of Object.entries(r?.option_prices ?? {})) {
    const price = num(value);
    if (price !== null) optionPrices[Number(id)] = price;
  }
  return {
    handlingFee: num(r?.handling_fee),
    shippingFee: num(r?.shipping_fee),
    salesTax: num(r?.sales_tax),
    note: r?.note ?? null,
    optionPrices,
  };
}

/**
 * POST /pool_shippings — create a delivery from the chosen address, items
 * and service. Maps the flat iframe params onto the Rails nested-attributes
 * contract (`pool_shipping_items_attributes` / `pool_shipping_positions_attributes`).
 */
export async function createPoolShipping(
  client: ApiClient,
  params: CreatePoolShippingParams,
): Promise<IframePoolShipping> {
  const body = {
    pool_shipping: {
      address_id: params.addressId,
      pool_shipping_items_attributes: params.poolItemIds.map((poolItemId) => ({
        pool_item_id: poolItemId,
      })),
      pool_shipping_positions_attributes: params.poolShippingServiceIds.map(
        (poolShippingServiceId) => ({ pool_shipping_service_id: poolShippingServiceId }),
      ),
    },
  };
  const raw = await client.request<RawCreatedShipping | undefined>(BASE, {
    method: 'POST',
    body,
  });
  return mapShipping(raw?.pool_shipping ?? {});
}
